import React, { useState, useEffect } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { getFormattedDateTime, useToast } from "../../services/utils";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import ButtonComponent from "../../components/Button.Component";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { deleteUserGroup, reactivateGroup } from "../../services/groupService";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  IconEditBase,
  IconDeleteBlueSvg,
  IconEyeBase,
  IconAssignUsersBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import usePaginatedFetchGroupsList from "../../hooks/usePaginatedFetchGroupsList";
import useDebounce from "../../hooks/useDebounce";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import Loader from "../../components/Molecules/Loader/Loader";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { useAppTranslation } from "../../hooks/useAppTranslation";

// Centralized Filter Configuration for Groups List
const GROUPS_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    group_name: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["group_name", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const Groups: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const { t } = useAppTranslation();
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const { rowsPerPage } = useRowsPerPage();

  // Permission checks for group actions
  const canViewGroup = hasPermission("user_group", "view");
  const canAddGroup = hasPermission("user_group", "add");
  const canEditGroup = hasPermission("user_group", "edit");
  const canDeleteGroup = hasPermission("user_group", "delete");
  const canAssignUsers = hasPermission("user_group_users", "view");

  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...GROUPS_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    GROUPS_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [groupToDelete, setGroupToDelete] = useState<any>(null);

  // Extract filters from searchFilterData
  const effectiveStatusFilter = searchFilterData?.status_filter || "";
  const effectiveGroupName = searchFilterData?.group_name || "";

  // Debounce group_name with 400ms delay
  const debouncedGroupName = useDebounce(effectiveGroupName);

  // Determine if we should include group_name in API call
  const effectiveGroupNameSearch =
    debouncedGroupName.trim().length >= 3 ? debouncedGroupName : "";

  // Convert status_filter to isActive parameter
  let isActiveParam = "true"; // Default to active
  if (effectiveStatusFilter === "Inactive") {
    isActiveParam = "false";
  } else if (effectiveStatusFilter === "All") {
    isActiveParam = "all";
  }

  const [groupsData, refetchGroups] = usePaginatedFetchGroupsList({
    setIsLoading,
    page,
    pSize,
    searchTerm: effectiveGroupNameSearch,
    isActive: isActiveParam,
  });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    GROUPS_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...GROUPS_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    GROUPS_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? ""
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? ""
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes to prevent infinite loops
    if (hasFilterChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [effectiveGroupNameSearch, effectiveStatusFilter]);

  // Handle delete group
  const handleDeleteGroup = (group: any) => {
    setGroupToDelete(group);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!groupToDelete) return;

    try {
      setIsLoading(true);
      await deleteUserGroup(groupToDelete.id);
      showToast(t("groups.toast.deleted"), "success");
      setDeleteConfirmOpen(false);
      setGroupToDelete(null);
      // Refetch the groups list to update the UI
      refetchGroups();
    } catch (error: any) {
      console.error("Error deleting group:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reactivate group
  const handleReactivate = async (group: any) => {
    try {
      setIsLoading(true);
      await reactivateGroup(group.id);
      showToast(t("groups.toast.reactivated"), "success");
      // Refetch the groups list to update the UI
      refetchGroups();
    } catch (error: any) {
      console.error("Error reactivating group:", error);
      showToast(
        error?.response?.data?.detail || t("groups.toast.failedReactivate"),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Handle assign users
  const handleAssignUsers = (group: any) => {
    navigate(`/groups/${group.id}/assign-users`);
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: t("groups.list.columns.id"),
      width: 100,
      sortable: true,
    },
    {
      field: "name",
      headerName: t("groups.list.columns.name"),
      width: 100,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params.value || ""} />
      ),
    },
    {
      field: "users",
      headerName: t("groups.list.columns.users"),
      width: 120,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => <span>{params.value?.length || 0}</span>,
    },
    {
      field: "roles",
      headerName: t("groups.list.columns.roles"),
      width: 120,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => <span>{params.value?.length || 0}</span>,
    },
    {
      field: "tags",
      headerName: t("groups.list.columns.tags"),
      width: 120,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => <span>{params.value?.length || 0}</span>,
    },
    {
      field: "modified_by_user",
      headerName: t("groups.list.columns.modifiedBy"),
      width: 150,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params?.value?.username} />
      ),
    },
    {
      field: "updated_on",
      headerName: t("groups.list.columns.updatedOn"),
      width: 150,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => {
        return getFormattedDateTime(params.value);
      },
    },
    {
      field: "is_active",
      headerName: t("groups.list.columns.status"),
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params: any) => {
        return params.value ? (
          <Tooltip
            title={t("groups.list.tooltip.active")}
            placement="top"
            arrow
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCheckCircleIconGreenSvg width={21} height={21} />
            </Box>
          </Tooltip>
        ) : (
          <Tooltip
            title={t("groups.list.tooltip.inactive")}
            placement="top"
            arrow
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconCrossCircleIconSvg width={21} height={21} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "actions",
      headerName: t("groups.list.columns.actions"),
      align: "center",
      headerAlign: "center",
      width: 160,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const isGroupActive = params.row.is_active;

        // For inactive groups, show ONLY the Reactivate button
        if (!isGroupActive) {
          return (
            <>
              {canEditGroup && (
                <Tooltip
                  title={t("groups.list.tooltip.reactivate")}
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleReactivate(params.row)}
                  >
                    <IconReactivateSvg />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        }

        // For active groups, show all action buttons based on permissions
        return (
          <>
            {canViewGroup && (
              <Tooltip
                title={t("groups.list.tooltip.view")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => navigate(`/groups/view/${params.row.id}`)}
                >
                  <IconEyeBase />
                </IconButton>
              </Tooltip>
            )}
            {canEditGroup && (
              <Tooltip
                title={t("groups.list.tooltip.edit")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => navigate(`/groups/edit/${params.row.id}`)}
                >
                  <IconEditBase />
                </IconButton>
              </Tooltip>
            )}
            {canAssignUsers && (
              <Tooltip
                title={t("groups.list.tooltip.assignUsers")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleAssignUsers(params.row)}
                >
                  <IconAssignUsersBase />
                </IconButton>
              </Tooltip>
            )}
            {canDeleteGroup && (
              <Tooltip
                title={t("groups.list.tooltip.delete")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleDeleteGroup(params.row)}
                >
                  <IconDeleteBlueSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      <Loader isLoading={isLoading} />

      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("groups.dialog.deleteTitle")}
        dailogDescription={t("groups.dialog.deleteDescription", {
          name: groupToDelete?.name,
        })}
      />

      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["group_name", "status_filter"]}
              primaryFields={["group_name", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          {canAddGroup && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={() => navigate("/groups/add")}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} />{" "}
                  {t("groups.list.title")}
                </>
              </ButtonComponent>
            </Grid>
          )}
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={groupsData?.items || []}
          loading={isLoading}
          dataListTitle={t("groups.list.dataListTitle")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={groupsData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          fitViewport
          singlePageMaxHeightDiff={358}
        />
      </FlexBetween>
    </Box>
  );
};

export default Groups;
